"use client"

import type React from "react"

import { useState, useEffect } from "react"
import type { CommonFormPropsType } from "../../../../Types/OtherPages/OtherPages"
import { Form, FormGroup, Input, Label, Spinner, Modal, ModalBody } from "reactstrap"
import { Btn, H4, P } from "../../../../AbstractElements"
import {
  CreateAccount,
  EmailAddress,
  EmailsPlaceHolder,
  ForgotPassword,
  Password,
  RememberPassword,
  SignIn,
  SignInAccount,
} from "../../../../utils/Constant"
import { Link, useNavigate, useLocation } from "react-router-dom"
import CommonLogo from "./CommonLogo"
import AuthService from "../../../../Service/AuthService"
import { toast } from "react-toastify"
import { jwtDecode } from "jwt-decode"
import ExhibitionSwitcherService from "../../../../Service/ExhibitionSwitcherService"
import { setActiveExhibition } from "../../../../utils/exhibitionContext"

interface TokenPayload {
  is_superuser: boolean;
  user_id: number;
}

/**
 * After a successful login (token is already stored) decide where the user
 * should land:
 *  - Superusers → dashboard immediately (no exhibition restriction)
 *  - 0 active exhibitions → dashboard (admins handle this edge case)
 *  - 1 active exhibition  → auto-select it, go to dashboard
 *  - 2+ active exhibitions → /exhibition-switcher so the user picks one
 *
 * Returns the destination path string.
 */
async function resolvePostLoginDestination(): Promise<string> {
  const DASHBOARD = `${process.env.PUBLIC_URL || ""}/dashboard`
  const SWITCHER  = `${process.env.PUBLIC_URL || ""}/exhibition-switcher`

  try {
    const token = localStorage.getItem("accessToken") || ""
    const decoded = jwtDecode<TokenPayload>(token)

    // Superusers skip exhibition selection
    if (decoded.is_superuser) return DASHBOARD

    const activeExhibitions = await ExhibitionSwitcherService.getActiveExhibitions()

    if (activeExhibitions.length === 0) {
      // Nothing to select – let them in; the backend middleware handles the fallback
      return DASHBOARD
    }

    if (activeExhibitions.length === 1) {
      // Auto-select the only active exhibition – no prompt needed
      setActiveExhibition(activeExhibitions[0])
      return DASHBOARD
    }

    // Multiple active exhibitions → show the switcher page
    return SWITCHER
  } catch (err) {
    console.warn("resolvePostLoginDestination error:", err)
    return DASHBOARD
  }
}

const LoginForm = ({ alignLogo }: CommonFormPropsType) => {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [otp, setOtp] = useState("")
  const [step, setStep] = useState<"login" | "otp">("login")
  const [loading, setLoading] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState("")


  const [checkingSession, setCheckingSession] = useState(true)

  const [redirectState, setRedirectState] = useState<{
    isRedirecting: boolean
    message: string
  }>({
    isRedirecting: false,
    message: "",
  })

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    checkExistingSession()
  }, [])

  // ✅ NEW: Professional redirect handler
  const handleProfessionalRedirect = (message: string, destination?: string) => {
    console.log("🚀 LoginForm: Starting professional redirect...")
    setRedirectState({
      isRedirecting: true,
      message,
    })

    setTimeout(async () => {
      const path = destination ?? await resolvePostLoginDestination()
      window.location.href = path
    }, 1500)
  }

  const checkExistingSession = () => {
    setCheckingSession(true)
    try {
      const hasValidSession = AuthService.hasValidSession()

      if (hasValidSession) {
       
        toast.success("Welcome back!")
        handleProfessionalRedirect("We found your active session. Redirecting you to dashboard...")
        return
      } else {
        // Clear any invalid/expired session data
        const token = AuthService.getToken()
        if (token) {
          
          AuthService.logout()
          toast.info("Session expired. Please login again.")
        } else {
          console.log("🔍 LoginForm: No existing session found")
        }
      }
    } catch (error) {
      console.error("❌ LoginForm: Session validation error:", error)
      AuthService.logout()
    } finally {
      setCheckingSession(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      console.log("🔐 LoginForm: Starting login for:", email)
      const response = await AuthService.loginStep1(email, password)

      if (response?.access) {
        const decoded = AuthService.getDecodedToken()
        console.log("✅ LoginForm: Login successful, decoded token:", decoded)

        if (decoded) {
          toast.success("Login successful!")
          // Resolve exhibition context before redirecting
          const destination = await resolvePostLoginDestination()
          handleProfessionalRedirect("Login successful! Setting up your workspace...", destination)
        } else {
          console.error("❌ LoginForm: Could not decode token")
          toast.error("Invalid login response")
        }
      } else {
        // Use the phone number returned from the backend to notify the user where the OTP was sent
        const phone = response?.phone
        if (phone) {
          setPhoneNumber(phone)
          toast.info(`OTP sent to your phone number: ${phone}`)
        }
        setStep("otp")
      }
    } catch (err: any) {
      console.error("❌ LoginForm: Login error:", err)
      if (err?.response?.data) {
        const errors = err.response.data
        if (errors?.message?.includes("Failed to send SMS")) {
          toast.error(errors.message || "SMS sending failed. Please try again later.")
        } else {
          const firstKey = Object.keys(errors)[0]
          toast.error(errors[firstKey][0] || "Login failed. Please try again.")
        }
      } else if (err?.message) {
        toast.error(`An error occurred: ${err.message || "Please check your connection and try again."}`)
      } else {
        toast.error("Login failed. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      console.log("🔐 LoginForm: Verifying OTP for:", email)
      const response = await AuthService.loginStep2(email, otp)
      const decoded = AuthService.getDecodedToken()

      if (decoded && AuthService.isTokenValid()) {
        toast.success("Verification successful!")
        // Resolve exhibition context after OTP verification too
        const destination = await resolvePostLoginDestination()
        handleProfessionalRedirect("Verification complete! Preparing your dashboard...", destination)
      } else {
        toast.error("Invalid or expired token")
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "OTP verification failed")
    } finally {
      setLoading(false)
    }
  }

  // ✅ NEW: Show professional redirect modal
  if (redirectState.isRedirecting) {
    return (
      <Modal isOpen={true} centered backdrop="static" keyboard={false}>
        <ModalBody className="text-center py-5">
          <div className="mb-4">
            <Spinner color="success" size="lg" />
          </div>
          <h4 className="text-success mb-3">
            <i className="fa fa-check-circle me-2"></i>
            Success!
          </h4>
          <p className="text-muted mb-3">{redirectState.message}</p>
          <div className="d-flex justify-content-center align-items-center">
            <Spinner size="sm" className="me-2" />
            <small className="text-muted">Please wait a moment...</small>
          </div>
          <div className="mt-3">
            <div className="progress" style={{ height: "4px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                role="progressbar"
                style={{ width: "100%" }}
              ></div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    )
  }


  return (
    <div className="login-card login-dark">
      <div>
        <CommonLogo alignLogo={alignLogo} />
        <div className="login-main">
          {step === "login" ? (
            <Form className="theme-form" onSubmit={handleLogin}>
              <H4>{SignInAccount}</H4>
              <P>Enter your email & password to login</P>
              <FormGroup>
                <Label className="col-form-label">{EmailAddress}</Label>
                <Input
                  type="email"
                  required
                  placeholder={EmailsPlaceHolder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label className="col-form-label">{Password}</Label>
                <div className="form-input position-relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="*********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="show-hide">
                    <span onClick={() => setShowPassword(!showPassword)} className={!showPassword ? "show" : ""} />
                  </div>
                </div>
              </FormGroup>
              <FormGroup className="mb-0 position-relative">
                <div className="checkbox p-0">
                  <Input id="checkbox1" type="checkbox" />
                  <Label className="text-muted" for="checkbox1">
                    {RememberPassword}
                  </Label>
                </div>
                <Link className="forgot-link" to={`/auth/forgetpassword`}>
                  {ForgotPassword}
                </Link>
                <div className="text-end mt-3">
                  <Btn color="primary" block disabled={loading}>
                    {loading ? <Spinner size="sm" /> : SignIn}
                  </Btn>
                </div>
              </FormGroup>
            </Form>
          ) : (
            <Form className="theme-form" onSubmit={handleOtpSubmit}>
              <H4>Enter OTP</H4>
              <P>An OTP has been sent to {phoneNumber}. Please enter it below.</P>
              <FormGroup>
                <Label className="col-form-label">OTP Code</Label>
                <Input
                  type="text"
                  required
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </FormGroup>
              <Btn color="primary" block disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Verify OTP"}
              </Btn>
            </Form>
          )}

          {/* <P className="mt-4 mb-0 text-center">
            {"Don't have account?"}
            <Link className="ms-2" to={`/auth/signup/`}>
              {CreateAccount}
            </Link>
          </P> */}
        </div>
      </div>
    </div>
  )
}

export default LoginForm
